import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiErrorMessage } from '../api';
import { help } from '../helpTexts';
import { cardSx, chipButtonSx, launchColors, overlineSx } from '../launchTheme';
import { Assignee } from '../types';
import { AssetHoverPreview } from '../ui/AssetHoverLink';
import AthenaMark from '../ui/AthenaMark';
import InfoTip from '../ui/InfoTip';
import KpiTile from '../ui/KpiTile';
import ListSkeleton from '../ui/ListSkeleton';
import SearchField from '../ui/SearchField';
import { StatusPill } from '../ui/StatusPill';
import AlcoholCard from './AlcoholCard';
import AlcoholImportDialog from './AlcoholImportDialog';
import { alcoholApi } from './alcoholApi';
import { categoryTone, pill, QUICK_FILTERS, STATE_TONES, STATUS_TONES } from './alcoholLabels';
import { AlcoholFacet, AlcoholFilter, AlcoholList, AlcoholPrompts, AlcoholSummary, STATE_LABELS, STATES, STATUS_LABELS } from './alcoholTypes';
import NewAlcoholDialog from './NewAlcoholDialog';
import ProducersPanel from './ProducersPanel';
import PromptSettingsDialog from './PromptSettingsDialog';

interface Props {
  canEdit: boolean;
  canAdmin: boolean;
  people?: Assignee[];
  openProductId?: string | null;
  onOpened?: () => void;
}

const NO_PEOPLE: Assignee[] = [];
const EMPTY_FILTER: AlcoholFilter = { q: '', status: '', state: '', category: '', country: '', producer: '', assortment: '', quick: '' };
const NO_CATEGORY = 'Utan kategori';
const NO_PRODUCER = 'Utan producent';
const NO_COUNTRY = 'Utan land';
export type AlcoholGrouping = 'category' | 'producer' | 'country';

const groupName = (product: AlcoholSummary, grouping: AlcoholGrouping) => {
  if (grouping === 'producer') return product.producer || NO_PRODUCER;
  if (grouping === 'country') return product.country || NO_COUNTRY;
  return product.category || NO_CATEGORY;
};

export function groupProducts(products: AlcoholSummary[], grouping: AlcoholGrouping): Array<{ name: string; products: AlcoholSummary[] }> {
  const blank = grouping === 'producer' ? NO_PRODUCER : grouping === 'country' ? NO_COUNTRY : NO_CATEGORY;
  const groups: Array<{ name: string; products: AlcoholSummary[] }> = [];
  const byName = new Map<string, AlcoholSummary[]>();
  for (const product of products) {
    const name = groupName(product, grouping);
    let items = byName.get(name);
    if (!items) {
      items = [];
      byName.set(name, items);
      groups.push({ name, products: items });
    }
    items.push(product);
  }
  groups.sort((left, right) => (left.name === blank ? 1 : right.name === blank ? -1 : left.name.localeCompare(right.name, 'sv')));
  for (const group of groups) {
    group.products.sort((left, right) => left.articleNumber.localeCompare(right.articleNumber, 'sv', { numeric: true }));
  }
  return groups;
}

export function groupByCategory(products: AlcoholSummary[]): Array<{ name: string; products: AlcoholSummary[] }> {
  return groupProducts(products, 'category');
}

const moveFocus = (event: React.KeyboardEvent<HTMLElement>) => {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-alcohol-row]'));
  const index = rows.indexOf(event.currentTarget);
  const next = event.key === 'ArrowDown' ? rows[index + 1]
    : event.key === 'ArrowUp' ? rows[index - 1]
      : event.key === 'Home' ? rows[0]
        : event.key === 'End' ? rows[rows.length - 1]
          : undefined;
  if (next && next !== event.currentTarget) {
    event.preventDefault();
    next.focus();
  }
};

const Dot: React.FC<{ color: string; size?: number }> = ({ color, size = 10 }) => (
  <Box component="span" aria-hidden sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0, display: 'inline-block' }} />
);

const AlcoholRow = React.memo(function AlcoholRow({ product, selected, onSelect }: { product: AlcoholSummary; selected: boolean; onSelect: (id: string) => void }) {
  const ready = product.completeness.missing.length === 0;
  const status = STATUS_TONES[product.status];
  const state = STATE_TONES[product.state];
  const attention = product.warnings.some((warning) => warning.kind === 'duplicate' || warning.kind === 'too_long' || warning.kind === 'ean_check');
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderTop: `1px solid ${launchColors.rowDivider}`,
        borderLeft: selected ? `3px solid ${launchColors.navy}` : '3px solid transparent',
        bgcolor: selected ? launchColors.softNavy : 'transparent',
        opacity: product.state === 'cancelled' ? 0.6 : 1,
        '&:hover': { bgcolor: selected ? launchColors.softNavy : launchColors.section },
      }}
    >
      <ButtonBase
        data-alcohol-row
        onClick={() => onSelect(product.id)}
        onKeyDown={moveFocus}
        aria-current={selected ? 'true' : undefined}
        aria-label={`${product.articleNumber} ${product.name || 'Namn saknas'}, ${STATUS_LABELS[product.status]}, ${STATE_LABELS[product.state]}`}
        sx={{
          flex: 1,
          minWidth: 0,
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 0.75,
          textAlign: 'left',
          '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: -2 },
        }}
      >
        <Dot color={categoryTone(product.category).dot} />
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, minWidth: 52 }}>
          {product.athenaAssetId ? (
            <AssetHoverPreview assetId={product.athenaAssetId} title={product.name || product.articleNumber} hasThumbnail>{product.articleNumber}</AssetHoverPreview>
          ) : product.articleNumber}
        </Typography>
        <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography component="span" sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: product.name ? launchColors.text : launchColors.textMuted }}>
            {product.name || 'Namn saknas'}
          </Typography>
          {product.producer && (
            <Typography component="span" sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: launchColors.textSecondary }}>{product.producer}</Typography>
          )}
        </Box>
        {attention && <WarningAmberIcon titleAccess="Har varningar" sx={{ fontSize: 16, color: launchColors.warningMarker }} />}
        {!ready && (
          <Typography component="span" title={`Saknas: ${product.completeness.missing.join(', ')}`} sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {product.completeness.done}/{product.completeness.total}
          </Typography>
        )}
        {product.state !== 'current' && <StatusPill label={STATE_LABELS[product.state]} {...pill(state)} />}
        {product.status !== 'approved' && <StatusPill label={STATUS_LABELS[product.status]} {...pill(status)} />}
      </ButtonBase>
      {product.athenaAssetId && (
        <Box sx={{ pr: 1 }}>
          <AthenaMark assetId={product.athenaAssetId} articleNumber={product.articleNumber} />
        </Box>
      )}
    </Box>
  );
});

const withCurrent = (facets: AlcoholFacet[], value: string) => (value && !facets.some((item) => item.name === value) ? [...facets, { name: value, count: 0 }] : facets);

const AlcoholInfoView: React.FC<Props> = ({ canEdit, canAdmin, people = NO_PEOPLE, openProductId, onOpened }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [filter, setFilter] = useState<AlcoholFilter>(EMPTY_FILTER);
  const [query, setQuery] = useState('');
  const [data, setData] = useState<AlcoholList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [producersOpen, setProducersOpen] = useState(false);
  const [prompts, setPrompts] = useState<AlcoholPrompts | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [cardKey, setCardKey] = useState(0);
  const [grouping, setGrouping] = useState<AlcoholGrouping>('category');
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [unsaved, setUnsaved] = useState<string[]>([]);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const names = useMemo(() => Object.fromEntries(people.map((person) => [person.id, person.name])), [people]);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter((current) => (current.q === query ? current : { ...current, q: query })), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    alcoholApi.list(filter)
      .then((next) => {
        if (cancelled) return;
        setData(next);
        setError(null);
      })
      .catch((requestError) => { if (!cancelled) setError(apiErrorMessage(requestError, 'Produktinfo Alkohol kunde inte hämtas.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter, refresh]);

  useEffect(() => {
    alcoholApi.settings().then((settings) => setPrompts(settings.prompts)).catch(() => setPrompts(null));
  }, []);

  useEffect(() => {
    if (!openProductId) return;
    setSelectedId(openProductId);
    onOpened?.();
  }, [onOpened, openProductId]);

  const reload = useCallback(() => setRefresh((value) => value + 1), []);
  const groups = useMemo(() => groupProducts(data?.products ?? [], grouping), [data, grouping]);
  const selectProduct = (id: string) => {
    if (unsaved.length > 0 && id !== selectedId) setPendingId(id);
    else setSelectedId(id);
  };
  const facets = data?.facets ?? { categories: [], countries: [], producers: [], assortments: [] };
  const stats = data?.stats ?? { approved: 0, coming: 0, notCurrent: 0, producers: 0 };
  const setField = (key: keyof AlcoholFilter, value: string) => setFilter((current) => ({ ...current, [key]: value }));
  const filtered = Object.values(filter).some(Boolean);
  const showOnly = (next: Partial<AlcoholFilter>) => {
    setQuery('');
    setFilter({ ...EMPTY_FILTER, ...next });
  };

  const exportCsv = async () => {
    try {
      const blob = await alcoholApi.exportCsv(filter);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `produktinfo-alkohol-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Exporten misslyckades.'));
    }
  };

  const toggleGroup = (name: string) => setCollapsed((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]));

  const select = (key: keyof AlcoholFilter, label: string, all: string, options: AlcoholFacet[], width: number) => (
    <TextField select size="small" label={label} value={filter[key]} onChange={(event) => setField(key, event.target.value)} sx={{ flex: `0 0 ${width}px`, bgcolor: launchColors.card }}>
      <MenuItem value="">{all}</MenuItem>
      {withCurrent(options, filter[key]).map((item) => <MenuItem key={item.name} value={item.name}>{item.name}{item.count ? ` (${item.count})` : ''}</MenuItem>)}
    </TextField>
  );

  const listPane = (
    <Box sx={[cardSx, { position: 'relative', overflow: 'hidden', opacity: loading && data ? 0.6 : 1 }]}>
      {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, height: 2 }} />}
      {!data ? <ListSkeleton /> : (
      <Box role="list" aria-label="Produkter">
        {groups.map((group) => {
          const open = !collapsed.includes(group.name);
          const tone = grouping === 'category' ? categoryTone(group.name === NO_CATEGORY ? '' : group.name) : { dot: launchColors.navy, fg: launchColors.navy, bg: launchColors.softNavy };
          return (
            <Box key={group.name} role="listitem">
              <ButtonBase
                onClick={() => toggleGroup(group.name)}
                aria-expanded={open}
                sx={{ width: '100%', justifyContent: 'flex-start', gap: 0.75, px: 1, py: 0.75, bgcolor: launchColors.section, borderTop: `1px solid ${launchColors.divider}` }}
              >
                {open ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                <Dot color={tone.dot} />
                <Typography component="span" sx={[overlineSx, { flex: 1, textAlign: 'left', color: tone.fg }]}>{group.name}</Typography>
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary }}>{group.products.length}</Typography>
              </ButtonBase>
              {open && group.products.map((product) => (
                <AlcoholRow key={product.id} product={product} selected={product.id === selectedId} onSelect={selectProduct} />
              ))}
            </Box>
          );
        })}
      </Box>
      )}
      {data && data.products.length === 0 && (
        <Typography sx={{ p: 2, fontSize: 13, color: launchColors.textSecondary }}>
          {data.total === 0 ? 'Inga produkter ännu.' : 'Inga produkter matchar filtret.'}
        </Typography>
      )}
    </Box>
  );

  const card = selectedId && data ? (
    <AlcoholCard
      key={cardKey}
      productId={selectedId}
      fields={data.fields}
      facets={facets}
      prompts={prompts}
      canEdit={canEdit}
      names={names}
      athenaAssetId={data.products.find((product) => product.id === selectedId)?.athenaAssetId}
      onChanged={reload}
      onBack={narrow ? () => selectProduct('') : undefined}
      onOpen={selectProduct}
      onUnsaved={setUnsaved}
    />
  ) : (
    <Box sx={[cardSx, { p: 3, textAlign: 'center', color: launchColors.textSecondary }]}>
      <Typography sx={{ fontSize: 14 }}>Välj en produkt i listan.</Typography>
      {stats.coming > 0 && (
        <Button sx={{ mt: 1.5 }} onClick={() => showOnly({ quick: 'coming' })}>
          {`Visa de ${stats.coming} som väntar på godkännande`}
        </Button>
      )}
    </Box>
  );

  const hiddenFilters = [filter.status, filter.state, filter.assortment].filter(Boolean).length;
  const hiddenChips = [
    filter.status === 'approved' || filter.status === 'coming' ? { key: 'status' as const, label: STATUS_LABELS[filter.status] } : null,
    STATES.includes(filter.state as typeof STATES[number]) ? { key: 'state' as const, label: STATE_LABELS[filter.state as typeof STATES[number]] } : null,
    filter.assortment ? { key: 'assortment' as const, label: filter.assortment } : null,
  ].filter((item): item is { key: 'status' | 'state' | 'assortment'; label: string } => item !== null);

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
        <KpiTile
          value={stats.approved}
          label="Godkända"
          fg={STATUS_TONES.approved.fg}
          bg={launchColors.card}
          accent={STATUS_TONES.approved.dot}
          icon={<CheckCircleOutlineIcon />}
          info={help.alcoholApproved}
          hint="Visa godkända"
          zeroText={null}
          onClick={() => showOnly({ status: 'approved' })}
        />
        <KpiTile
          value={stats.coming}
          label="Väntar på godkännande"
          fg={STATUS_TONES.coming.fg}
          bg={stats.coming ? STATUS_TONES.coming.bg : launchColors.card}
          accent={STATUS_TONES.coming.dot}
          icon={<HourglassEmptyIcon />}
          info={help.alcoholComing}
          hint="Visa kommande"
          onClick={() => showOnly({ quick: 'coming' })}
        />
        <KpiTile
          value={stats.notCurrent}
          label="Inte aktuella"
          fg={launchColors.textSecondary}
          bg={launchColors.card}
          accent={launchColors.textMuted}
          icon={<DoNotDisturbOnOutlinedIcon />}
          info={help.alcoholNotCurrent}
          hint="Visa inte aktuella"
          onClick={() => showOnly({ quick: 'notCurrent' })}
        />
        <KpiTile
          value={stats.producers}
          label="Producenter"
          fg={launchColors.navy}
          bg={launchColors.card}
          accent={launchColors.navy}
          icon={<StorefrontOutlinedIcon />}
          info={help.alcoholProducers}
          hint="Visa producenter"
          zeroText={null}
          onClick={() => setProducersOpen(true)}
        />
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchField
          label="Sök"
          placeholder="Nummer, namn, producent, druva eller region"
          value={query}
          inputRef={searchRef}
          onChange={setQuery}
          sx={{ flex: '1 1 220px', minWidth: 0, bgcolor: launchColors.card }}
        />
        {select('category', 'Kategori', 'Alla kategorier', facets.categories, 160)}
        {select('country', 'Land', 'Alla länder', facets.countries, 140)}
        {select('producer', 'Producent', 'Alla producenter', facets.producers, 180)}
        <Button aria-expanded={Boolean(moreAnchor)} aria-haspopup="dialog" onClick={(event) => setMoreAnchor(event.currentTarget)} sx={chipButtonSx(hiddenFilters > 0)}>
          {hiddenFilters ? `Fler filter (${hiddenFilters})` : 'Fler filter'}
        </Button>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto' }}>
          {canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)} sx={{ whiteSpace: 'nowrap' }}>Ny produkt</Button>}
          <Button aria-label="Mer" aria-haspopup="menu" startIcon={<MoreHorizIcon />} onClick={(event) => setMenuAnchor(event.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>Mer</Button>
          <InfoTip text={help.alcoholInfo} />
        </Stack>
      </Stack>
      {hiddenChips.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {hiddenChips.map((item) => (
            <Button key={item.key} size="small" aria-label={`Ta bort filtret ${item.label}`} endIcon={<CloseIcon sx={{ fontSize: 16 }} />} onClick={() => setField(item.key, '')} sx={chipButtonSx(true)}>
              {item.label}
            </Button>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        {QUICK_FILTERS.map((item) => {
          const count = data?.stats.quick?.[item.value];
          return (
            <Button key={item.value} aria-pressed={filter.quick === item.value} onClick={() => setField('quick', filter.quick === item.value ? '' : item.value)} sx={chipButtonSx(filter.quick === item.value)}>
              {item.label}{count === undefined ? '' : ` (${count})`}
            </Button>
          );
        })}
        <Button size="small" onClick={() => { setQuery(''); setFilter(EMPTY_FILTER); }} tabIndex={filtered ? 0 : -1} aria-hidden={!filtered} sx={{ visibility: filtered ? 'visible' : 'hidden' }}>Rensa filter</Button>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={grouping} onChange={(_event, value: AlcoholGrouping | null) => { if (value) setGrouping(value); }} aria-label="Gruppera efter">
          <ToggleButton value="category">Kategori</ToggleButton>
          <ToggleButton value="producer">Producent</ToggleButton>
          <ToggleButton value="country">Land</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} message={notice || ''} />
      {data && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{data.products.length} av {data.total} produkter</Typography>
          <InfoTip text={help.alcoholLegend} />
        </Stack>
      )}
      {data && data.total === 0 && canAdmin && (
        <Alert severity="info" action={<Button onClick={() => setImportOpen(true)}>Läs in Excel</Button>}>
          Läs in Excel-filen med bladet PRODUKTER ALKOHOL för att komma igång.
        </Alert>
      )}
      {narrow ? (selectedId ? card : listPane) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.8fr) minmax(0, 1.2fr)', gap: 2, alignItems: 'start' }}>
          <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto', borderRadius: '12px' }}>{listPane}</Box>
          <Box sx={{ position: 'sticky', top: 0 }}>{card}</Box>
        </Box>
      )}
      <Popover open={Boolean(moreAnchor)} anchorEl={moreAnchor} onClose={() => setMoreAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Stack spacing={1.25} sx={{ p: 1.5, width: 260 }}>
          <TextField select size="small" label="Status" value={filter.status} onChange={(event) => setField('status', event.target.value)}>
            <MenuItem value="">Alla</MenuItem>
            <MenuItem value="approved">{STATUS_LABELS.approved}</MenuItem>
            <MenuItem value="coming">{STATUS_LABELS.coming}</MenuItem>
          </TextField>
          <TextField select size="small" label="Läge" value={filter.state} onChange={(event) => setField('state', event.target.value)}>
            <MenuItem value="">Alla lägen</MenuItem>
            {STATES.map((state) => <MenuItem key={state} value={state}>{STATE_LABELS[state]}</MenuItem>)}
          </TextField>
          {select('assortment', 'Sortiment', 'Alla sortiment', facets.assortments, 230)}
        </Stack>
      </Popover>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); setPromptsOpen(true); }}>Promptinställningar</MenuItem>
        <MenuItem disabled={!data?.products.length} onClick={() => { setMenuAnchor(null); void exportCsv(); }}>Exportera CSV</MenuItem>
        {canAdmin && <MenuItem onClick={() => { setMenuAnchor(null); setImportOpen(true); }}>Läs in Excel</MenuItem>}
      </Menu>
      <Dialog open={pendingId !== null} onClose={() => setPendingId(null)}>
        <DialogTitle>Texten har inte sparats</DialogTitle>
        <DialogContent>
          <Typography>{unsaved.join(', ')} är för lång och har inte sparats.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingId(null)}>Stanna</Button>
          <Button variant="contained" onClick={() => { setSelectedId(pendingId || null); setUnsaved([]); setPendingId(null); }}>Byt ändå</Button>
        </DialogActions>
      </Dialog>

      <NewAlcoholDialog
        open={newOpen}
        facets={facets}
        defaults={{ producer: filter.producer, category: filter.category }}
        onClose={() => setNewOpen(false)}
        onCreated={(detail) => {
          setNewOpen(false);
          setSelectedId(detail.product.id);
          setNotice(`Produkten ${detail.product.articleNumber} skapades.`);
          reload();
        }}
      />
      <AlcoholImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(message) => {
          setImportOpen(false);
          setNotice(message);
          reload();
        }}
      />
      <PromptSettingsDialog
        open={promptsOpen}
        canSave={canAdmin}
        names={names}
        onClose={() => setPromptsOpen(false)}
        onSaved={(settings) => {
          setPrompts(settings.prompts);
          setPromptsOpen(false);
          setNotice('Prompterna sparades.');
        }}
      />
      <ProducersPanel
        open={producersOpen}
        canEdit={canEdit}
        prompts={prompts}
        names={names}
        onClose={() => setProducersOpen(false)}
        onChanged={() => {
          reload();
          setCardKey((value) => value + 1);
        }}
        onShowProducts={(producer) => {
          setProducersOpen(false);
          showOnly({ producer });
        }}
      />
    </Stack>
  );
};

export default AlcoholInfoView;
